import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';

import { FBoxContainer, FHButton, FCheckbox, FHConfirmDialog, FProgressBar } from '../../../base';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { abaTherapyServicesActions, treatmentPlanActions } from '../../../../store';
import { TherapyServiceSchema } from './validationSchema';
import TherapyHistoryRow from './TherapyHistoryRow';
import { validateSync } from '../../../../helpers';
import { FORTA_WHITE } from '../../../../utils/colors';
import { Themes } from '../../../../constants';
import { EFrequency, errorStateConstants } from './constants';
import { TreatmentPlanPages } from '../constants/general';

import type { IAbaTherapyService } from '../../../../utils/types';
import type { ChangeEvent } from 'react';

function TherapyHistory() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const {
    treatmentPlanId: currentTreatmentPlanId,
    therapyHistory,
    services,
    loading,
    errorState,
  } = useSelector((state: any) => state.abaTherapyServices);
  const isEditable = useTreatmentPlanEditable();
  const [abaServices, setAbaServices] = useState<IAbaTherapyService[]>(services || []);
  const [addServiceDisabled, setAddServiceDisabled] = useState<boolean>(true);
  const [errors, setErrors] = useState<any>({});
  const [hasTherapyHistory, setHasTherapyHistory] = useState<boolean>(true);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  useEffect(() => {
    if (!treatmentPlan) {
      dispatch(abaTherapyServicesActions.resetState());
      return;
    }

    if (treatmentPlan?.id !== currentTreatmentPlanId) {
      dispatch(abaTherapyServicesActions.resetState());
    }

    dispatch(
      abaTherapyServicesActions.updateState({ key: 'treatmentPlanId', data: treatmentPlan?.id }),
    );
    dispatch(
      abaTherapyServicesActions.getAbaTherapy({
        treatmentPlanId: treatmentPlan.id,
        getAccessTokenSilently,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan]);

  useEffect(() => {
    setAbaServices(services);
    if (!therapyHistory || Object.keys(therapyHistory).length === 0) {
      return;
    }
    setErrors(0);
    setHasTherapyHistory(therapyHistory.has_service_history !== false);
  }, [therapyHistory, services]);

  useEffect(() => {
    if (abaServices?.some((service: IAbaTherapyService) => !Object.hasOwn(service, 'id'))) {
      setAddServiceDisabled(true);
      return;
    }

    setAddServiceDisabled(false);
  }, [abaServices]);

  useEffect(() => {
    updateErrorBadge(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    updateErrorBadge(errorState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorState]);

  useEffect(() => {
    if (!treatmentPlan?.id) return;

    let noServiceErrorCount = 0;
    if (hasTherapyHistory && services?.length < 1) {
      noServiceErrorCount = 1;
    } else {
      noServiceErrorCount = 0;
    }

    dispatch(
      abaTherapyServicesActions.updateErrorState({
        [errorStateConstants.noService]: noServiceErrorCount,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, hasTherapyHistory]);

  const updateErrorBadge = (errors: any) => {
    let totalErrorCount = 0;
    if (Number.isFinite(errors)) {
      totalErrorCount = errors;
    } else {
      for (const key in errors) {
        totalErrorCount += errors[key];
      }
    }

    dispatch(
      treatmentPlanActions.updateBadge({
        [TreatmentPlanPages.History]: totalErrorCount,
      }),
    );
  };

  const fetchCreateService = (service: IAbaTherapyService) => {
    if (!treatmentPlan.id || !therapyHistory.id) return;

    dispatch(
      abaTherapyServicesActions.createService({
        treatmentPlanId: treatmentPlan.id,
        therapyHistoryId: therapyHistory.id,
        data: service,
        getAccessTokenSilently,
      }),
    );
  };

  const fetchUpdateService = (service: IAbaTherapyService) => {
    if (!treatmentPlan.id || !therapyHistory.id || !service.id) return;

    dispatch(
      abaTherapyServicesActions.updateService({
        treatmentPlanId: treatmentPlan.id,
        therapyHistoryId: therapyHistory.id,
        data: service,
        getAccessTokenSilently,
      }),
    );
  };

  const handleRemoveService = (service: IAbaTherapyService) => {
    if (!service.id) {
      // remove services locally without calling server
      dispatch(
        abaTherapyServicesActions.updateErrorState({
          [`${errorStateConstants.services}-new`]: 0,
        }),
      );
      dispatch(
        abaTherapyServicesActions.updateEditing({
          actionType: 'delete',
        }),
      );
    }

    if (!treatmentPlan.id || !therapyHistory.id || !service.id) return;

    dispatch(
      abaTherapyServicesActions.removeService({
        treatmentPlanId: treatmentPlan.id,
        getAccessTokenSilently,
        therapyHistoryId: therapyHistory.id,
        serviceId: service.id,
      }),
    );
  };

  const handleAddServiceLocal = () => {
    if (!therapyHistory.id) {
      dispatch(
        abaTherapyServicesActions.createAbaTherapy({
          treatmentPlanId: treatmentPlan.id,
          getAccessTokenSilently,
          data: {
            has_service_history: true,
          },
        }),
      );
    }

    const newService = {
      frequency: EFrequency['weekly'],
      response_to_treatment: '',
      is_record_shared: null,
      is_parents_authorization_provided: true,
    };

    dispatch(
      abaTherapyServicesActions.updateEditing({
        treatmentPlanId: treatmentPlan.id,
        data: newService,
        actionType: 'create',
      }),
    );
  };

  const handleUpdateService = (service: IAbaTherapyService) => {
    const isValid = validateSync(TherapyServiceSchema, service);

    if (service && !service.id && !isValid) {
      dispatch(
        abaTherapyServicesActions.updateEditing({
          treatmentPlanId: treatmentPlan.id,
          data: service,
          actionType: 'update',
        }),
      );
    }

    if (!isValid) return;

    if (service) {
      if (service.id) {
        fetchUpdateService(service);
      } else {
        fetchCreateService(service);
      }
    }
  };

  const confirmServiceNeedChange = (notNeeded: boolean) => {
    if (services.length > 0 && notNeeded) {
      setShowConfirmation(true);
      return;
    }
    handleServiceNeedChange(notNeeded);
  };

  const handleServiceNeedChange = async (notNeeded: boolean) => {
    if (therapyHistory.id) {
      await dispatch(
        abaTherapyServicesActions.updateAbaTherapy({
          treatmentPlanId: treatmentPlan.id,
          getAccessTokenSilently,
          data: {
            id: therapyHistory.id,
            has_service_history: !notNeeded,
          },
        }),
      );
      setShowConfirmation(false);
    } else {
      await dispatch(
        abaTherapyServicesActions.createAbaTherapy({
          treatmentPlanId: treatmentPlan.id,
          getAccessTokenSilently,
          data: {
            has_service_history: !notNeeded,
          },
        }),
      );
      setShowConfirmation(false);
    }
  };

  return (
    <>
      {loading && <FProgressBar />}
      <Box sx={{ display: 'flex', flexDirection: 'column' }} mt={3}>
        <FBoxContainer
          colorTheme={Themes.Primary}
          title="ABA therapy and other services (historical and present)"
        >
          <Box sx={{ display: 'flex', mb: 2 }}>
            <FCheckbox
              data-testid="clientHasNoHistoryABACheckbox"
              colorTheme={Themes.Primary}
              checked={hasTherapyHistory === false}
              disabled={!isEditable}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                confirmServiceNeedChange(e.target.checked)
              }
              sx={{ mr: 0, fontWeight: 'bold' }}
            />
            <Typography fontWeight={'bold'}>
              Client has no history of ABA or other services
            </Typography>
          </Box>
          {hasTherapyHistory && (
            <Box>
              {abaServices.map((service: IAbaTherapyService, index: number) => {
                return (
                  <TherapyHistoryRow
                    key={index}
                    service={service}
                    onUpdate={handleUpdateService}
                    onRemove={() => handleRemoveService(service)}
                  />
                );
              })}
            </Box>
          )}

          {hasTherapyHistory && isEditable && (
            <Box sx={{ display: 'flex' }}>
              <FHButton
                data-testid="addServiceButton"
                colorTheme={Themes.Primary}
                startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
                onClick={handleAddServiceLocal}
                disabled={addServiceDisabled}
                error={abaServices?.length === 0}
              >
                Add Service
              </FHButton>
            </Box>
          )}
        </FBoxContainer>
        <FHConfirmDialog
          open={showConfirmation}
          colorTheme={Themes.Primary}
          content={
            'This action will delete ABA therapy and service history, are you sure you want to continue?'
          }
          submittext={'CONTINUE'}
          canceltext={'BACK'}
          onClose={() => setShowConfirmation(false)}
          onSubmit={() => handleServiceNeedChange(true)}
        />
      </Box>
    </>
  );
}

export default TherapyHistory;
